import { useTranslation } from '@mint-lib/i18n';
import {
  AiStarFilled,
  Download,
  StatusChange,
  TrashCan,
  Upload,
} from '@mint-lib/icons';
import { Button, Divider, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

type LegacySelectionProps = {
  canDownloadKBFile: boolean;
  canDeleteSelection: boolean;
  hasSelectedFolders: boolean;
  onDownloadSelection: () => void;
  onDeleteSelection: () => void;
};

type FileSystemSelectionProps = LegacySelectionProps & {
  canMoveSelection: boolean;
  onOpenMoveDialog: () => void;
};

type Props = FileSystemSelectionProps & {
  selectedCount: number;
  isFileSystemEnabled: boolean;
  canWrite: boolean;
  onOpenUploadDialog: () => void;
};

const FileSystemSelectionActions = ({
  canDownloadKBFile,
  canDeleteSelection,
  canMoveSelection,
  hasSelectedFolders,
  onDownloadSelection,
  onDeleteSelection,
  onOpenMoveDialog,
}: FileSystemSelectionProps) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <>
      {canMoveSelection && (
        <>
          <Button
            variant="outlined"
            data-dd-action-name="Knowledge base: move selection"
            onClick={onOpenMoveDialog}
          >
            <StatusChange />
            {t('Move to')}
          </Button>

          <Divider
            orientation="vertical"
            className="data-[orientation=vertical]:h-6"
          />
        </>
      )}

      <Button
        icon
        variant="text"
        aria-label={t('Download')}
        data-dd-action-name="Knowledge base: download selection"
        onClick={onDownloadSelection}
        disabled={!canDownloadKBFile || hasSelectedFolders}
      >
        <Download className="fill-text-primary" />
      </Button>

      <Button
        icon
        color="error"
        variant="text"
        aria-label={t('Delete')}
        data-dd-action-name="Knowledge base: delete selection"
        onClick={onDeleteSelection}
        disabled={!canDeleteSelection}
      >
        <TrashCan />
      </Button>
    </>
  );
};

const LegacySelectionActions = ({
  canDownloadKBFile,
  canDeleteSelection,
  hasSelectedFolders,
  onDownloadSelection,
  onDeleteSelection,
}: LegacySelectionProps) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        onClick={onDownloadSelection}
        disabled={!canDownloadKBFile || hasSelectedFolders}
      >
        <Download />
        {t('Download')}
      </Button>

      <Button
        color="error"
        variant="outlined"
        onClick={onDeleteSelection}
        disabled={!canDeleteSelection}
      >
        <TrashCan />
        {t('Delete')}
      </Button>
    </>
  );
};

/**
 * The leading slot of the Knowledge base toolbar: it carries the upload action
 * until rows are picked, then hands the space over to the selection actions.
 */
const ToolbarActions = ({
  selectedCount,
  isFileSystemEnabled,
  canWrite,
  onOpenUploadDialog,
  canMoveSelection,
  onOpenMoveDialog,
  ...selection
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  if (!selectedCount && !isFileSystemEnabled) {
    if (!canWrite) return null;

    return (
      <Button className="flex flex-row gap-2" onClick={onOpenUploadDialog}>
        <Upload />
        {t('Upload file')}
      </Button>
    );
  }

  if (!selectedCount) {
    return (
      <>
        <Button
          variant="outlined"
          className="bg-linear-to-tr from-ai-lightCyan to-ai-lightGreen text-text-inverse"
          disabled
        >
          <AiStarFilled />
          {t('Ask MintIQ')}
        </Button>

        {canWrite && (
          <Button
            variant="outlined"
            data-dd-action-name="Knowledge base: open upload dialog"
            onClick={onOpenUploadDialog}
          >
            <Upload />
            {t('Upload file')}
          </Button>
        )}
      </>
    );
  }

  const selectionActions = isFileSystemEnabled ? (
    <FileSystemSelectionActions
      {...selection}
      canMoveSelection={canMoveSelection}
      onOpenMoveDialog={onOpenMoveDialog}
    />
  ) : (
    <LegacySelectionActions {...selection} />
  );

  return (
    <div className="flex flex-row items-center gap-3">
      <p
        className={cn(
          typographyVariants({ variant: 'bodyShort01' }),
          'text-nowrap',
        )}
      >
        {t('{{selectedQty}} selected', { selectedQty: selectedCount })}
      </p>

      {canWrite && (
        <>
          <Divider
            orientation="vertical"
            className="data-[orientation=vertical]:h-6"
          />
          {selectionActions}
        </>
      )}
    </div>
  );
};

export { ToolbarActions };
